'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { curatorQueries } from '../query-factory';
import { CreateSnapCurationForm } from './CreateSnapCurationForm';
import { SelectSnapsSelectionStep } from './SelectSnapsSelectionStep';
import { useShopEditContext } from './ShopEditContext';
import { type ShopSnapCurationEditFormData, shopSnapCurationEditFormSchema } from './type';
import { getCurationFormDraftKey, useCurationFormDraft } from './useCurationFormDraft';

interface Props {
  className?: string;
}

export function ShopSnapCurationCreate({ className }: Props) {
  const { step, setStep } = useShopEditContext();
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';

  const goToSelectSnaps = () => {
    if (service === SERVICE.MUSINSA && curatorId) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'snap_add_try',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_curation_create',
        type: 'snap',
      });
    }
    setStep('step2');
  };

  const initialValues = useMemo<ShopSnapCurationEditFormData>(
    () => ({
      title: '',
      description: '',
      selectedSnaps: [],
    }),
    [],
  );

  const form = useForm<ShopSnapCurationEditFormData>({
    resolver: zodResolver(shopSnapCurationEditFormSchema),
    defaultValues: initialValues,
  });
  useCurationFormDraft(getCurationFormDraftKey('snap'), form, initialValues);

  return (
    <FormProvider {...form}>
      <div className={twMerge('flex w-full grow flex-col bg-neutral-color-white', className)}>
        <CreateSnapCurationForm
          className={step === 'step2' ? 'hidden' : ''}
          onClickSelectSnaps={goToSelectSnaps}
        />
        {step === 'step2' && (
          <SelectSnapsSelectionStep
            defaultCheckedSnaps={form.getValues('selectedSnaps')}
            onSubmit={() => setStep('step1')}
          />
        )}
      </div>
    </FormProvider>
  );
}
